// Pure Schema.org builders for catalog pages.
// Keep this module free of the DOM and React so an SSR or prerender pass (#743)
// can call the same functions and drop jsonLdScriptHtml() into the first HTML
// response. The <JsonLd> component renders that script inside the React tree,
// which renderToString keeps.

import { conditions } from './conditions'

export const HOLIC_ORIGIN = 'https://holic.net'

// One page of /units (Kaminari .per(32)). Crawlers should not have to follow
// infinite scroll to see the index that is on the first response.
export const JSON_LD_ITEM_LIST_LIMIT = 32

const IN_STOCK = 'https://schema.org/InStock'
const OUT_OF_STOCK = 'https://schema.org/OutOfStock'
const SOLD_OUT = 'https://schema.org/SoldOut'

export type JsonLdNode = {
  '@type': string
  [key: string]: unknown
}

export type JsonLdDocument = {
  '@context': 'https://schema.org'
  '@graph': JsonLdNode[]
}

export type JsonLdImage = {
  id?: number
  imageUrl?: string
  standard?: string
  thumb2x?: string
  ordinality?: number
}

export type JsonLdTag = {
  id: number
  type?: string
  depth?: number
  parentId?: number
  nameEn?: string
  nameJa?: string
  nameEnShort?: string
  nameJaShort?: string
  parent?: JsonLdTag
  parents?: readonly JsonLdTag[]
}

export type JsonLdItem = {
  id: number
  nameEn?: string
  nameJa?: string
  description?: string
  images?: JsonLdImage[]
  tags?: JsonLdTag[]
  holicListingsCount?: number
  holicMinimumPrice?: number
}

export type JsonLdListing = {
  platform?: string
  priceJpy?: number
  soldAt?: string
  quantity?: number
}

export type JsonLdUnit = {
  id?: number
  userId?: number
  condition?: number
  public?: boolean
  description?: string
  images?: JsonLdImage[]
  listings?: JsonLdListing[]
  items?: JsonLdItem[]
}

export type Crumb = {
  id?: number
  name: string
  path: string
}

type ConditionLabel = (condition: number) => string

const graph = (parts: Array<JsonLdNode | null | undefined>): JsonLdDocument | null => {
  const present = parts.filter((part): part is JsonLdNode => !!part)
  if (present.length === 0) return null

  return {
    '@context': 'https://schema.org',
    '@graph': present,
  }
}

export const siteOrigin = (explicit?: string): string => {
  if (explicit) return explicit.replace(/\/$/, '')

  const fromEnv = process.env.REACT_APP_SITE_ORIGIN
  if (fromEnv) return fromEnv.replace(/\/$/, '')
  if (process.env.REACT_APP_RAILS_ENV === 'production') return HOLIC_ORIGIN
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin

  return HOLIC_ORIGIN
}

export const absoluteUrl = (origin: string, path?: string): string | undefined => {
  if (!path) return undefined
  if (/^https?:\/\//i.test(path)) return path
  if (path.startsWith('//')) return `https:${path}`

  const base = origin.replace(/\/$/, '')
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`
}

// Card pages and listing headings use this expression (nameJa only when the UI
// language is Japanese). Set pages use setCrumbLabel, which prefers short names.
export const localizedName = (record: { nameEn?: string, nameJa?: string }, language: string): string => {
  const preferred = language === 'ja' && record.nameJa
  const showJapanese = preferred ?? !record.nameEn
  if (showJapanese) return record.nameJa ?? ''
  return record.nameEn ?? ''
}

export const setCrumbLabel = (tag: { nameEn?: string, nameJa?: string, nameEnShort?: string, nameJaShort?: string }, language: string): string => {
  let title = tag.nameEn
  if (language === 'ja' && (tag.nameJaShort ?? tag.nameJa)) {
    title = tag.nameJaShort ?? tag.nameJa
  } else if (tag.nameEnShort) {
    title = tag.nameEnShort
  }
  return title ?? ''
}

// Same walk as the card page breadcrumb: start at the deepest category and
// follow parentId through category.parents. A category with no parentId has
// no visible crumb trail.
export const cardBreadcrumbTags = <T extends { id: number, parentId?: number, parents?: readonly T[] }>(category: T | undefined): T[] => {
  if (!category?.parentId) return []

  const crumbs: T[] = []
  const seen = new Set<number>()
  let working: T | undefined = category

  while (working && !seen.has(working.id)) {
    seen.add(working.id)
    crumbs.unshift(working)
    const parentId = working.parentId
    working = category.parents?.find(tag => tag.id === parentId)
  }

  return crumbs
}

const tagsOfType = (tags: readonly JsonLdTag[] | undefined, type: string): JsonLdTag[] => {
  return (tags ?? []).filter(tag => tag.type === type)
}

const deepest = (tags: JsonLdTag[]): JsonLdTag | undefined => {
  if (tags.length === 0) return undefined
  return tags.reduce((prev, current) => (current.depth ?? 0) >= (prev.depth ?? 0) ? current : prev)
}

const listingTitle = (items: JsonLdItem[] | undefined, language: string): string => {
  return (items ?? [])
    .map(item => (language === 'ja' ? item.nameJa : item.nameEn) ?? '')
    .join(' / ')
    .trim()
}

const cleanText = (value?: string): string | undefined => {
  const text = value?.trim()
  return text ? text : undefined
}

const yen = (value: unknown): string | undefined => {
  const numeric = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== ''
      ? Number(value)
      : NaN
  if (!Number.isFinite(numeric) || numeric < 0) return undefined
  return String(Math.round(numeric))
}

const isHolicListing = (listing: JsonLdListing): boolean => {
  return !listing.platform || listing.platform === 'holic'
}

const availabilityFor = (listing: JsonLdListing): string => {
  if (listing.soldAt) return SOLD_OUT
  if (typeof listing.quantity === 'number' && listing.quantity <= 0) return OUT_OF_STOCK
  return IN_STOCK
}

const itemConditionUrl = (condition?: number): string | undefined => {
  if (!condition) return undefined
  if (condition >= 90) return 'https://schema.org/NewCondition'
  if (condition <= 10) return 'https://schema.org/DamagedCondition'
  return 'https://schema.org/UsedCondition'
}

const conditionName = (condition: number | undefined, conditionLabel?: ConditionLabel): string | undefined => {
  if (!condition) return undefined
  return conditionLabel?.(condition) || conditions.find(entry => entry.id === condition)?.name
}

const imageUrls = (images: readonly JsonLdImage[] | undefined, origin: string): string[] => {
  const sorted = [...(images ?? [])].sort((a, b) => (a.ordinality ?? 0) - (b.ordinality ?? 0) || (a.id ?? 0) - (b.id ?? 0))
  const urls: string[] = []

  for (const image of sorted) {
    const url = absoluteUrl(origin, image.imageUrl || image.standard || image.thumb2x)
    if (url && !urls.includes(url)) urls.push(url)
    if (urls.length >= 3) break
  }

  return urls
}

const unitImageUrls = (unit: JsonLdUnit, origin: string): string[] => {
  const own = imageUrls(unit.images, origin)
  if (own.length > 0) return own
  return imageUrls(unit.items?.[0]?.images, origin)
}

const property = (name: string, value?: string): JsonLdNode | undefined => {
  if (!value) return undefined
  return { '@type': 'PropertyValue', name, value }
}

const catalogFacts = (item: JsonLdItem, language: string) => {
  const setTag = deepest(tagsOfType(item.tags, 'Category'))
  const rarityTag = deepest(tagsOfType(item.tags, 'Rarity'))
  const additionalProperty = [
    property('set', setTag ? localizedName(setTag, language) : undefined),
    property('rarity', rarityTag ? localizedName(rarityTag, language) : undefined),
  ].filter((entry): entry is JsonLdNode => !!entry)

  return {
    categoryId: setTag?.id,
    setName: setTag ? localizedName(setTag, language) : undefined,
    additionalProperty,
  }
}

const offerForListing = (
  unit: JsonLdUnit,
  listing: JsonLdListing,
  origin: string,
  conditionLabel?: ConditionLabel,
): JsonLdNode | null => {
  const price = yen(listing.priceJpy)
  if (!price) return null

  const offer: JsonLdNode = {
    '@type': 'Offer',
    priceCurrency: 'JPY',
    price,
    availability: availabilityFor(listing),
  }
  const condition = itemConditionUrl(unit.condition)
  if (condition) offer.itemCondition = condition

  const label = conditionName(unit.condition, conditionLabel)
  if (label) {
    offer.additionalProperty = { '@type': 'PropertyValue', name: 'condition', value: label }
  }

  if (unit.userId && unit.id) {
    offer.url = absoluteUrl(origin, `/u/${unit.userId}/units/${unit.id}`)
  }

  return offer
}

const offersForUnit = (unit: JsonLdUnit, origin: string, conditionLabel?: ConditionLabel): JsonLdNode[] => {
  if (unit.public === false) return []

  return (unit.listings ?? [])
    .filter(isHolicListing)
    .map(listing => offerForListing(unit, listing, origin, conditionLabel))
    .filter((offer): offer is JsonLdNode => !!offer)
}

const aggregateOffer = (item: JsonLdItem, url?: string): JsonLdNode | null => {
  const count = item.holicListingsCount ?? 0
  const lowPrice = yen(item.holicMinimumPrice)
  if (count <= 0 || !lowPrice) return null

  const offer: JsonLdNode = {
    '@type': 'AggregateOffer',
    priceCurrency: 'JPY',
    lowPrice,
    offerCount: count,
  }
  if (url) offer.url = url
  return offer
}

const withOffers = (product: JsonLdNode, offers: JsonLdNode[]): JsonLdNode => {
  if (offers.length === 1) product.offers = offers[0]
  else if (offers.length > 1) product.offers = offers
  return product
}

const productNode = (input: {
  name: string
  sku: string
  url: string
  images: string[]
  description?: string
  facts?: ReturnType<typeof catalogFacts>
  offers: JsonLdNode[]
}): JsonLdNode => {
  const product: JsonLdNode = {
    '@type': 'Product',
    '@id': `${input.url}#product`,
    name: input.name,
    sku: input.sku,
    url: input.url,
  }

  if (input.images.length > 0) product.image = input.images
  if (input.description) product.description = input.description
  if (input.facts?.setName) product.category = input.facts.setName
  if (input.facts && input.facts.additionalProperty.length > 0) {
    product.additionalProperty = input.facts.additionalProperty
  }

  return withOffers(product, input.offers)
}

const breadcrumbList = (crumbs: Crumb[], origin: string): JsonLdNode | null => {
  const visible = crumbs.filter(crumb => crumb.name && crumb.path)
  if (visible.length < 2) return null

  const pageUrl = absoluteUrl(origin, visible[visible.length - 1].path)

  return {
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: visible.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(origin, crumb.path),
    })),
  }
}

const itemListNode = (elements: JsonLdNode[], origin: string, pagePath?: string, name?: string): JsonLdNode | null => {
  if (elements.length === 0) return null

  const node: JsonLdNode = {
    '@type': 'ItemList',
    itemListElement: elements,
  }
  const url = absoluteUrl(origin, pagePath)
  if (name) node.name = name
  if (url) {
    node.url = url
    node['@id'] = `${url}#itemlist`
  }
  return node
}

const organizationNode = (origin: string): JsonLdNode => ({
  '@type': 'Organization',
  '@id': `${origin}/#organization`,
  name: 'HOLIC',
  alternateName: 'ホリック',
  url: `${origin}/`,
  logo: {
    '@type': 'ImageObject',
    url: `${origin}/favicons/android-icon-192x192.png`,
    width: 192,
    height: 192,
  },
  email: 'support@holic.net',
})

const parentChain = (category: JsonLdTag): JsonLdTag[] => {
  const crumbs: JsonLdTag[] = []
  const seen = new Set<number>()
  let working: JsonLdTag | undefined = category

  while (working && !seen.has(working.id)) {
    seen.add(working.id)
    crumbs.unshift(working)
    const parent = working.parent
    working = parent && working.parentId === parent.id ? parent : undefined
  }

  return crumbs
}

const categoryBranch = (tags: readonly JsonLdTag[] | undefined): JsonLdTag[] => {
  const category = deepest(tagsOfType(tags, 'Category'))
  if (!category) return []
  if (category.parents?.length) return cardBreadcrumbTags(category)
  return parentChain(category)
}

const cardCrumbs = (item: JsonLdItem, language: string, categoryId?: number): Crumb[] => {
  const category = deepest(tagsOfType(item.tags, 'Category'))
  const trail = cardBreadcrumbTags(category).map(tag => ({
    id: tag.id,
    name: localizedName(tag, language),
    path: `/categories/${tag.id}`,
  }))
  const pageCategoryId = categoryId ?? category?.id
  const name = localizedName(item, language)
  if (!name || !pageCategoryId) return trail

  return [
    ...trail,
    {
      id: item.id,
      name,
      path: `/categories/${pageCategoryId}/items/${item.id}`,
    },
  ]
}

export type CardPageInput = {
  origin?: string
  language: string
  item: JsonLdItem
  categoryId?: number
  // undefined: listings have not loaded, so holicListingsCount may be used.
  // an array: authoritative, including an empty list. Do not revive a stale InStock offer.
  units?: JsonLdUnit[]
  conditionLabel?: ConditionLabel
}

export const cardPageJsonLd = (input: CardPageInput): JsonLdDocument | null => {
  const origin = siteOrigin(input.origin)
  const name = localizedName(input.item, input.language)
  if (!name) return null

  const facts = catalogFacts(input.item, input.language)
  const categoryId = input.categoryId ?? facts.categoryId ?? 1
  const path = `/categories/${categoryId}/items/${input.item.id}`
  const url = absoluteUrl(origin, path) as string
  const offers = input.units
    ? input.units.flatMap(unit => offersForUnit(unit, origin, input.conditionLabel))
    : [aggregateOffer(input.item, url)].filter((offer): offer is JsonLdNode => !!offer)

  return graph([
    breadcrumbList(cardCrumbs(input.item, input.language, categoryId), origin),
    productNode({
      name,
      sku: String(input.item.id),
      url,
      images: imageUrls(input.item.images, origin),
      description: cleanText(input.item.description),
      facts,
      offers,
    }),
  ])
}

export type ListingPageInput = {
  origin?: string
  language: string
  unit: JsonLdUnit
  conditionLabel?: ConditionLabel
}

export const listingPageJsonLd = (input: ListingPageInput): JsonLdDocument | null => {
  if (input.unit.public === false || !input.unit.id || !input.unit.userId) return null

  const origin = siteOrigin(input.origin)
  const item = input.unit.items?.[0]
  const name = item ? localizedName(item, input.language) : ''
  if (!name) return null

  const facts = item ? catalogFacts(item, input.language) : undefined
  const path = `/u/${input.unit.userId}/units/${input.unit.id}`
  const url = absoluteUrl(origin, path) as string
  const categoryCrumbs = item
    ? categoryBranch(item.tags).map(tag => ({
      id: tag.id,
      name: localizedName(tag, input.language),
      path: `/categories/${tag.id}`,
    }))
    : []

  return graph([
    breadcrumbList([
      ...categoryCrumbs,
      { name, path },
    ], origin),
    productNode({
      name,
      sku: `unit-${input.unit.id}`,
      url,
      images: unitImageUrls(input.unit, origin),
      description: cleanText(input.unit.description) ?? (item ? cleanText(item.description) : undefined),
      facts,
      offers: offersForUnit(input.unit, origin, input.conditionLabel),
    }),
  ])
}

export type BrowsePageInput = {
  origin?: string
  language: string
  home: boolean
  crumbs: Crumb[]
  view: string
  items?: JsonLdItem[]
  listings?: JsonLdUnit[]
  fallbackCategoryId?: number
  conditionLabel?: ConditionLabel
}

const listingElements = (
  listings: JsonLdUnit[],
  origin: string,
  language: string,
  conditionLabel?: ConditionLabel,
): JsonLdNode[] => {
  const elements: JsonLdNode[] = []

  for (const unit of listings) {
    if (elements.length >= JSON_LD_ITEM_LIST_LIMIT) break
    if (unit.public === false || !unit.id || !unit.userId) continue

    const name = listingTitle(unit.items, language)
    if (!name) continue

    const item = unit.items?.[0]
    const path = `/u/${unit.userId}/units/${unit.id}`
    const url = absoluteUrl(origin, path) as string
    elements.push({
      '@type': 'ListItem',
      position: elements.length + 1,
      name,
      url,
      item: productNode({
        name,
        sku: `unit-${unit.id}`,
        url,
        images: unitImageUrls(unit, origin),
        description: cleanText(unit.description),
        facts: item ? catalogFacts(item, language) : undefined,
        offers: offersForUnit(unit, origin, conditionLabel),
      }),
    })
  }

  return elements
}

const cardElements = (
  items: JsonLdItem[],
  origin: string,
  language: string,
  fallbackCategoryId?: number,
): JsonLdNode[] => {
  const elements: JsonLdNode[] = []

  for (const item of items) {
    if (elements.length >= JSON_LD_ITEM_LIST_LIMIT) break

    const name = localizedName(item, language)
    if (!name) continue

    const facts = catalogFacts(item, language)
    const categoryId = facts.categoryId ?? fallbackCategoryId ?? 1
    const path = `/categories/${categoryId}/items/${item.id}`
    const url = absoluteUrl(origin, path) as string
    const aggregate = aggregateOffer(item, url)

    elements.push({
      '@type': 'ListItem',
      position: elements.length + 1,
      name,
      url,
      item: productNode({
        name,
        sku: String(item.id),
        url,
        images: imageUrls(item.images, origin),
        description: cleanText(item.description),
        facts,
        offers: aggregate ? [aggregate] : [],
      }),
    })
  }

  return elements
}

export const browsePageJsonLd = (input: BrowsePageInput): JsonLdDocument | null => {
  const origin = siteOrigin(input.origin)
  const pagePath = input.crumbs[input.crumbs.length - 1]?.path ?? (input.home ? '/' : undefined)
  const listName = input.crumbs[input.crumbs.length - 1]?.name
  const elements = input.view === 'items'
    ? cardElements(input.items ?? [], origin, input.language, input.fallbackCategoryId)
    : listingElements(input.listings ?? [], origin, input.language, input.conditionLabel)

  return graph([
    input.home ? organizationNode(origin) : null,
    breadcrumbList(input.crumbs, origin),
    itemListNode(elements, origin, pagePath, listName),
  ])
}

export const serializeJsonLd = (data: unknown): string => {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
}

export const jsonLdScriptHtml = (data: unknown): string => {
  return `<script type="application/ld+json">${serializeJsonLd(data)}</script>`
}
